import {
  type useContraProStripeCheckoutCreateSubscriptionCheckoutLinkMutation,
  type useContraProStripeCheckoutCreateSubscriptionCheckoutLinkMutation$data,
} from '#app/__generated__/relay/useContraProStripeCheckoutCreateSubscriptionCheckoutLinkMutation.graphql.js';
import { type useContraProStripeCheckoutRefreshIndependentPortfolioPlanSubscriptionMutation } from '#app/__generated__/relay/useContraProStripeCheckoutRefreshIndependentPortfolioPlanSubscriptionMutation.graphql.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { usePersistedReferralIntent } from '#app/hooks/usePersistedReferralIntent.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { graphql } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { useCallback, useEffect, useState } from 'react';

export type RedirectToStripeCheckoutType = (options: {
  successParameters: {
    justSubscribedToProCustomBrandingModalOpen?: boolean;
    justSubscribedToProModalOpen?: boolean;
    justSubscribedViaJobFeedAccessRequest?: boolean;
  };
}) => Promise<void>;

export const createStripeCheckoutUrls = (
  currentURL: string,
  options: Parameters<RedirectToStripeCheckoutType>[0],
): { cancelUrl: string; successUrl: string } => {
  const { successParameters } = options;

  const baseReturnUrl = new URL(currentURL);
  baseReturnUrl.searchParams.set('refresh', 'true');

  if (baseReturnUrl.searchParams.has('upgradeToPro')) {
    baseReturnUrl.searchParams.delete('upgradeToPro');
  }

  const cancelUrl = baseReturnUrl.href;

  const successReturnUrl = new URL(baseReturnUrl);

  if (successParameters) {
    for (const [key, value] of Object.entries(successParameters)) {
      successReturnUrl.searchParams.set(key, String(value));
    }
  }

  const successUrl = successReturnUrl.href;

  return { cancelUrl, successUrl };
};

export const useContraProStripeCheckout = () => {
  const toast = useToast();
  const location = useLocation();
  const navigation = useNavigation();
  const [urlSearchParameters] = useURLParameters('*');
  const { referralIntent } = usePersistedReferralIntent();
  const needRefresh = urlSearchParameters.refresh;

  const [isRefreshed, setIsRefreshed] = useState<boolean>(false);

  const [createStripeCheckoutSessionLink, isGeneratingStripeLink] =
    useContraMutation<useContraProStripeCheckoutCreateSubscriptionCheckoutLinkMutation>(
      graphql`
        mutation useContraProStripeCheckoutCreateSubscriptionCheckoutLinkMutation(
          $input: CreateSubscriptionCheckoutLinkInput!
        ) {
          createSubscriptionCheckoutLink(input: $input) {
            errors {
              __typename
              message
            }
            url
          }
        }
      `,
    );

  const redirectToStripeCheckout = useCallback<RedirectToStripeCheckoutType>(
    async (options) => {
      const { cancelUrl, successUrl } = createStripeCheckoutUrls(
        location.currentURL,
        options,
      );

      return await new Promise((resolve, reject) => {
        createStripeCheckoutSessionLink({
          onCompleted: (
            errors,
            response: useContraProStripeCheckoutCreateSubscriptionCheckoutLinkMutation$data,
          ) => {
            const { apiErrors, hasErrors, payloadErrors } = errors;
            const {
              createSubscriptionCheckoutLink: { url },
            } = response;

            if (hasErrors || !url) {
              if (payloadErrors) {
                toast.error('An unexpected error occurred. Please try again.');
                captureException(payloadErrors[0]);
              } else if (apiErrors) {
                toast.error(apiErrors[0].message);
              } else if (!url) {
                toast.error('An unexpected error occurred. Please try again.');
                captureException(
                  new Error(
                    'No contra pro stripe checkout url returned from mutation.',
                  ),
                );
              }
            } else {
              navigation.push(url);
            }
          },
          onError: reject,
          variables: {
            input: {
              cancelUrl,
              referralIntentId: referralIntent?.id,
              successUrl,
            },
          },
        });
      });
    },
    [
      createStripeCheckoutSessionLink,
      location,
      navigation,
      referralIntent,
      toast,
    ],
  );

  const [refreshSubscriptionMutation] =
    useContraMutation<useContraProStripeCheckoutRefreshIndependentPortfolioPlanSubscriptionMutation>(
      graphql`
        mutation useContraProStripeCheckoutRefreshIndependentPortfolioPlanSubscriptionMutation {
          refreshIndependentPortfolioPlanSubscription {
            errors {
              __typename
              message
            }
            visitor {
              id
              userAccount {
                independentPortfolioPlanSubscription {
                  status
                }
                profile {
                  hasActivePortfolioSubscription
                }
              }
            }
          }
        }
      `,
    );

  const refreshSubscription = useCallback(async (): Promise<void> => {
    return await new Promise((resolve, reject) => {
      refreshSubscriptionMutation({
        onCompleted: (errors) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;

          if (hasErrors) {
            if (payloadErrors) {
              toast.error('An unexpected error occurred. Please try again.');
              captureException(payloadErrors[0]);
              reject(payloadErrors[0].message);
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
              reject(apiErrors[0].message);
            }
          } else {
            resolve();
          }
        },
        onError: reject,
        variables: {},
      });
    });
  }, [refreshSubscriptionMutation, toast]);

  useEffect(() => {
    void (async () => {
      if (needRefresh && !isRefreshed) {
        await refreshSubscription();

        setIsRefreshed(true);
      } else {
        setIsRefreshed(true);
      }
    })();
  }, [isRefreshed, needRefresh, refreshSubscription, setIsRefreshed]);

  return {
    isGeneratingStripeLink,
    redirectToStripeCheckout,
    refreshSubscription,
  };
};
